/**
 * W7 — the *asking* half of the product: what the Taskize extractor returns, and what the
 * notification publisher answers.
 *
 * `ai-actions/taskize` uses the bare-`{data}` convention (a plain object whose top key happens
 * to be `data`), NOT the `{data, policies}` envelope — see W7 §B1. `ai-actions/notify` is bare.
 *
 * ⚠ **No row carries a `sourceAnchor`.** `TaskizeTaskDto` / `TaskizeDecisionDto` have no such
 * property and the server never sends one (W7 §0 C6), so click-quote-to-scroll and per-part
 * scoping are affordances slate must NOT build. The quote renders as static text.
 */
import { z } from "zod";

/** Below this a row arrives unchecked and wears a "low" chip. The server does not send it. */
export const CONFIDENCE_PRECHECK = 0.7;

export const TaskizeTaskSchema = z.object({
  title: z.string(),
  details: z.string().nullable().default(null),
  assigneeHint: z.string().nullable().default(null),
  dueHint: z.string().nullable().default(null),
  sourceQuote: z.string().nullable().default(null),
  /** Non-nullable `double` server-side; everything else on the row is nullable. */
  confidence: z.number().default(0),
});
export type TaskizeTask = z.infer<typeof TaskizeTaskSchema>;

export const TaskizeDecisionSchema = z.object({
  statement: z.string(),
  status: z.string().nullable().default(null),
  decidedByHint: z.string().nullable().default(null),
  dateHint: z.string().nullable().default(null),
  sourceQuote: z.string().nullable().default(null),
  confidence: z.number().default(0),
});
export type TaskizeDecision = z.infer<typeof TaskizeDecisionSchema>;

export const TaskizeReminderSchema = z.object({
  title: z.string(),
  whenHint: z.string().nullable().default(null),
  sourceQuote: z.string().nullable().default(null),
  confidence: z.number().default(0),
});
export type TaskizeReminder = z.infer<typeof TaskizeReminderSchema>;

export const TaskizeResultSchema = z.object({
  tasks: z.array(TaskizeTaskSchema).default([]),
  decisions: z.array(TaskizeDecisionSchema).default([]),
  reminders: z.array(TaskizeReminderSchema).default([]),
  /** Which thinker answered — shown in the tray header, because provenance is the point. */
  thinkerId: z.string().nullable().default(null),
  thinkerName: z.string().nullable().default(null),
  model: z.string().nullable().default(null),
  source: z.string().nullable().default(null),
});
export type TaskizeResult = z.infer<typeof TaskizeResultSchema>;

export interface TaskizeInput {
  documentId: string;
  collectionId?: string;
  documentTitle?: string;
  /** Blank ⇒ the server loads the whole document (v2 lane first). Blank both ⇒ 400. */
  selectedText?: string;
  thinkerId?: string;
}

export const NotifyOutcomeSchema = z.object({
  published: z.boolean().default(false),
  /** Frozen taxonomy id — the Hub's notification kinds were fixed in Wave 1. */
  kind: z.string().nullable().default(null),
  audienceRole: z.string().nullable().default(null),
  documentId: z.string().nullable().default(null),
  collectionId: z.string().nullable().default(null),
});
export type NotifyOutcome = z.infer<typeof NotifyOutcomeSchema>;

export interface NotifyInput {
  message: string;
  audienceRole?: "admin" | "member" | "viewer";
  documentId?: string;
  collectionId?: string;
}

export const EMPTY_TASKIZE: TaskizeResult = {
  tasks: [],
  decisions: [],
  reminders: [],
  thinkerId: null,
  thinkerName: null,
  model: null,
  source: null,
};
